// ============================================================
// Single Product View
// Loads one product at a time (page_length=1, start=N).
// Prev/Next trigger a new API request — fast, no bulk loading.
// ============================================================

webshop.ProductSingle = class {
	/*
	 * Options:
	 *   query_args       – filters object from get_query_filters()
	 *   settings         – Webshop Settings doc
	 *   products_section – jQuery wrapper for #products-single-area
	 *   preference       – current view preference string
	 */
	constructor(options) {
		Object.assign(this, options);

		if (this.preference !== "Single View") {
			this.products_section.addClass("hidden");
			return;
		}

		this.current_index = 0;  // 0-based offset
		this.total_count   = 0;
		this.products_section.empty();
		this._fetch(0);
	}

	// ── API ──────────────────────────────────────────────────────

	_fetch(start) {
		const me = this;
		this._set_loading(true);

		// Build clean args — always override start, page_length, and from_filters.
		// from_filters must be false so the API never resets start back to 0.
		const args = Object.assign({}, this.query_args, {
			page_length: 1,
			start: start,
			from_filters: false,
		});

		frappe.call({
			method: "webshop.webshop.api.get_product_filter_data",
			args: { query_args: args },
			callback(result) {
				me._set_loading(false);
				if (!result || result.exc || !result.message || result.message.exc) return;

				const msg = result.message;
				if (!msg.items || !msg.items.length) return;

				me.total_count = msg.items_count || 1;
				me.settings    = msg.settings;
				me.current_index = start;

				me._render_item(msg.items[0]);
			}
		});
	}

	// ── Shell (built once) ───────────────────────────────────────

	_ensure_shell() {
		if (this.products_section.find(".ws-single-view").length) return;

		this.products_section.append(`
			<div class="ws-single-view">
				<button class="ws-single-nav ws-single-prev" disabled>
					<svg class="icon icon-lg"><use href="#icon-left"></use></svg>
				</button>
				<div class="ws-single-card-wrap"></div>
				<button class="ws-single-nav ws-single-next">
					<svg class="icon icon-lg"><use href="#icon-right"></use></svg>
				</button>
			</div>
		`);

		this._bind_nav();
		this._bind_qty_events();
	}

	_set_loading(on) {
		if (on) {
			this._ensure_shell();
			this.products_section.find(".ws-single-card-wrap").html(`
				<div style="display:flex;flex-direction:column;width:100%;min-height:600px;border-radius:var(--ws-radius);overflow:hidden;box-shadow:var(--ws-shadow);">
					<div class="ws-skel" style="flex:0 0 320px;width:100%;border-radius:0;"></div>
					<div style="flex:1;background:#fff;padding:24px 28px;display:flex;flex-direction:column;gap:12px;">
						<div class="ws-skel" style="height:12px;width:30%;border-radius:4px;"></div>
						<div class="ws-skel" style="height:22px;width:80%;border-radius:4px;"></div>
						<div class="ws-skel" style="height:14px;width:40%;border-radius:4px;"></div>
						<div class="ws-skel" style="height:28px;width:25%;border-radius:4px;margin-top:8px;"></div>
						<div class="ws-skel" style="height:12px;width:60%;border-radius:4px;margin-top:8px;"></div>
					</div>
				</div>
			`);
			// Disable nav while loading
			this.products_section.find(".ws-single-nav").prop("disabled", true);
		}
	}

	// ── Card rendering ───────────────────────────────────────────

	_render_item(item) {
		this._ensure_shell();

		const title = (item.web_item_name || item.item_name || item.item_code || "")
			.substr(0, 200);

		this.products_section.find(".ws-single-card-wrap")
			.html(this._get_card_html(item, title));

		// Nav state
		const i = this.current_index;
		const n = this.total_count;
		this.products_section.find(".ws-single-prev").prop("disabled", i === 0);
		this.products_section.find(".ws-single-next").prop("disabled", i >= n - 1);
		this.products_section.find(".ws-single-counter").text(`${ i + 1 } / ${ n }`);
	}

	_get_card_html(item, title) {
		return `
			<div class="ws-single-card">
				${ this._get_image_html(item, title) }
				${ this._get_body_html(item, title) }
			</div>
		`;
	}

	_get_image_html(item, title) {
		const img = item.website_image
			? `<img itemprop="image" class="ws-single-img"
					src="${ item.website_image }" alt="${ title }" loading="eager">`
			: `<div class="ws-single-no-image">${ frappe.get_abbr(title) }</div>`;

		return `
			<a href="/${ item.route || '#' }" class="ws-single-img-link">
				${ img }
			</a>
		`;
	}

	_get_body_html(item, title) {
		const settings = this.settings || {};

		// Price
		let price_html = '';
		if (item.formatted_price) {
			price_html = `
				<div class="ws-single-price">
					${ item.formatted_mrp
						? `<span class="ws-single-mrp">${ item.formatted_mrp }</span>` : '' }
					<span class="ws-single-rate">${ item.formatted_price }</span>
					${ item.discount_percent
						? `<span class="ws-single-discount">-${ item.discount_percent }%</span>` : '' }
				</div>`;
		}

		// Bulk prices
		const bulk_html = item.bulk_prices_html
			? `<div class="bulk-prices-wrapper ws-single-bulk">${ item.bulk_prices_html }</div>` : '';

		// Stock
		const stock_html = this._get_stock_html(item, settings);

		// Cart action
		const cart_html = this._get_cart_html(item, settings);

		// Wishlist
		const wish_html = (!item.has_variants && settings.enable_wishlist)
			? this._get_wishlist_html(item) : '';

		// Item group
		const group_html = (item.item_group_display || item.item_group)
			? `<div class="ws-single-group">${ item.item_group_display || item.item_group }</div>` : '';

		return `
			<div class="ws-single-body">
				<div class="ws-single-header">
					<div style="flex:1;min-width:0;">
						${ group_html }
						<a href="/${ item.route || '#' }" class="ws-single-title-link">
							<h2 class="ws-single-title">${ title }</h2>
						</a>
						<div class="ws-single-code">${ __("Item Code") }: ${ item.item_code }</div>
					</div>
					${ wish_html }
				</div>

				${ item.short_description
					? `<div class="ws-single-desc">${ item.short_description }</div>` : '' }

				${ price_html }
				${ bulk_html }
				${ stock_html }
				${ cart_html }

				<div class="ws-single-meta">
					<span class="ws-single-counter"></span>
					<a href="/${ item.route || '#' }" class="ws-single-detail-link">
						${ __("View Details") } →
					</a>
				</div>
			</div>
		`;
	}

	_get_stock_html(item, settings) {
		if (!settings.show_stock_availability || item.has_variants) return '';
		if (item.on_backorder)
			return `<span class="out-of-stock mt-2" style="color:var(--primary-color)">
				${ __("Available on backorder") }</span>`;
		if (!item.in_stock)
			return `<span class="out-of-stock mt-2">${ __("Out of Stock") }</span>`;
		return '';
	}

	_get_cart_html(item, settings) {
		if (item.has_variants) {
			return `
				<div class="mt-3">
					<a href="/${ item.route || '#' }">
						<div class="btn btn-sm btn-explore-variants">${ __("Explore") }</div>
					</a>
				</div>`;
		}
		if (!settings.enabled || (!settings.allow_items_not_in_stock && !item.in_stock)) return '';

		const visible = item.in_cart;
		const badge = `
			<div class="cart-indicator-slot">
				<a href="/cart" class="cart-indicator-link" style="${ visible ? '' : 'visibility:hidden;' }">
					<span class="cart-indicator-badge" data-item-code="${ item.item_code }">
						${ visible ? item.in_cart_qty + ' ' + __("in cart") : '&nbsp;' }
					</span>
				</a>
			</div>`;

		return `
			<div class="cart-action-row mt-3">
				${ badge }
				<div class="item-qty-area">
					<div class="input-group number-spinner">
						<span class="input-group-prepend d-sm-inline-block">
							<button class="item-qty-btn dwn-qty" data-dir="dwn">&ndash;</button>
						</span>
						<input class="form-control text-center item-qty"
							value="${ item.min_qty || 1 }"
							min="${ item.min_qty || 1 }"
							step="${ item.lot_size || 1 }"
							data-item-code="${ item.item_code }"
							data-min-qty="${ item.min_qty || 1 }"
							data-lot-size="${ item.lot_size || 1 }">
						<span class="input-group-append d-sm-inline-block">
							<button class="item-qty-btn up-qty" data-dir="up">+</button>
						</span>
					</div>
				</div>
				<div id="${ item.name }" class="btn btn-sm btn-primary btn-add-to-cart-list"
					data-item-code="${ item.item_code }">
					<svg class="icon icon-md"><use href="#icon-assets"></use></svg>
					${ settings.enable_checkout ? __("Add to Cart") : __("Add to Quote") }
				</div>
			</div>`;
	}

	_get_wishlist_html(item) {
		const cls = item.wished ? "wished" : "not-wished";
		return `
			<div class="like-action ws-single-wish ${ cls }" data-item-code="${ item.item_code }">
				<svg class="icon icon-md"><use href="#icon-heart"></use></svg>
			</div>`;
	}

	// ── Navigation ───────────────────────────────────────────────

	_bind_nav() {
		const me = this;
		this.products_section.on("click", ".ws-single-prev", () => {
			if (me.current_index > 0) me._fetch(me.current_index - 1);
		});
		this.products_section.on("click", ".ws-single-next", () => {
			if (me.current_index < me.total_count - 1) me._fetch(me.current_index + 1);
		});
	}

	// ── Qty events ───────────────────────────────────────────────

	_bind_qty_events() {
		this.products_section.on("input", ".item-qty-area .item-qty", (e) => {
			e.target.value = e.target.value.replace(/[^0-9]/g, "");
		});
		this.products_section.on("click", ".item-qty-area .up-qty", (e) => {
			const $q = $(e.target).closest(".item-qty-area").find(".item-qty");
			const step = parseInt($q.data("lot-size")) || 1;
			$q.val((parseInt($q.val()) || 0) + step);
		});
		this.products_section.on("click", ".item-qty-area .dwn-qty", (e) => {
			const $q = $(e.target).closest(".item-qty-area").find(".item-qty");
			const step = parseInt($q.data("lot-size")) || 1;
			const minQty = parseInt($q.data("min-qty")) || 1;
			$q.val(Math.max((parseInt($q.val()) || 0) - step, minQty));
		});
	}
};
